import {
    collectActors,
    getActorAvatar,
    getDramaCover,
    getDramaEpisodes,
    getDramaHlsVideo,
    getDramaTitle,
    getDramaVideo,
    normalizeStatus,
    resolveAssetUrl,
    sumEpisodePlayCount,
} from '@/pages/drama-review/utils';

import type { DramaItem } from '@/services/dramas';
import type {
    BannerEditorItem,
    BannerItemSnapshot,
    DramaItemRef,
    TheaterBannerConfig,
    TheaterBannerConfigItem,
} from '@/types/theater-banner';

import { THEATER_BANNER_MAX_ITEMS } from '@/constants/theater-banner';

import { extractMiniDramaAssetPath, resolveMiniDramaCdnUrl } from '@/utils/mini-drama-assets';

// ---------------------------------------------------------------------------
// 内部工具
// ---------------------------------------------------------------------------

/** 剧集列表资源：相对路径走 WEB2_ASSETS，完整 URL 原样返回 */
const resolveDramaListAssetUrl = (path?: string): string => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return resolveAssetUrl(path);
};

/**
 * 把上传后的 URL（S3 直链或 CDN URL）转成最终写入 config 的 CDN URL。
 * 只在运营主动上传时调用。
 */
const toCdnUrl = (url?: string): string => {
    if (!url?.trim()) return '';
    return resolveMiniDramaCdnUrl(extractMiniDramaAssetPath(url));
};

const readNumber = (...values: unknown[]): number | undefined => {
    for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const num = Number(value);
        if (!Number.isNaN(num)) return num;
    }
    return undefined;
};

const readString = (...values: unknown[]): string | undefined => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return undefined;
};

const readTags = (drama?: DramaItemRef | null): string[] | undefined => {
    if (!drama) return undefined;
    const raw = drama.tags ?? drama.tag_list;
    if (!Array.isArray(raw)) return undefined;
    const tags = raw.map((tag) => String(tag).trim()).filter(Boolean);
    return tags.length ? tags : undefined;
};

// ---------------------------------------------------------------------------
// 公共工具
// ---------------------------------------------------------------------------

export const getDramaBannerUrl = (drama?: DramaItemRef | null) =>
    readString(drama?.banner_url, drama?.bannerUrl, getDramaCover(drama as DramaItem)) ?? '';

export const extractSnapshotFromConfigItem = (item: TheaterBannerConfigItem): BannerItemSnapshot => ({
    title: item.title,
    description: item.description,
    tags: item.tags,
    totalEpisodes: item.totalEpisodes,
    creatorName: item.creatorName,
    totalPlayCount: item.totalPlayCount,
    totalHeatValue: item.totalHeatValue,
    avgRating: item.avgRating,
    actorAvatarUrls: item.actorAvatarUrls,
    bannerUrl: item.bannerUrl,
    thumbUrl: item.thumbUrl,
    previewHlsUrl: item.previewHlsUrl,
    previewVideoUrl: item.previewVideoUrl,
});

export const extractDramaBannerFields = (drama: DramaItem) => {
    const bannerUrl = resolveDramaListAssetUrl(getDramaBannerUrl(drama));
    const thumbUrl = resolveDramaListAssetUrl(getDramaCover(drama));
    const previewHlsUrl = resolveDramaListAssetUrl(getDramaHlsVideo(drama));
    const previewVideoUrl = resolveDramaListAssetUrl(getDramaVideo(drama));
    const episodes = getDramaEpisodes(drama);
    const actors = collectActors(drama);
    const actorAvatarUrls = actors.map((actor) => resolveDramaListAssetUrl(getActorAvatar(actor))).filter(Boolean);

    return {
        title: getDramaTitle(drama),
        description: drama.description ?? '',
        bannerUrl: bannerUrl || thumbUrl,
        thumbUrl: thumbUrl || bannerUrl,
        previewHlsUrl: previewHlsUrl || undefined,
        previewVideoUrl: previewVideoUrl || undefined,
        tags: readTags(drama),
        totalEpisodes: readNumber(drama.total_episodes, drama.episode_count, drama.totalEpisodes, episodes.length),
        creatorName: readString(
            drama.creator_name,
            drama.creatorName,
            drama.user_name,
            drama.creator?.name,
            (drama.creator as { nickname?: string } | undefined)?.nickname,
            drama.owner_name,
            drama.username,
        ),
        totalPlayCount: readNumber(
            drama.total_play_count,
            drama.play_count,
            drama.totalPlayCount,
            episodes.length ? sumEpisodePlayCount(episodes) : undefined,
        ),
        totalHeatValue: readNumber(drama.total_heat_value, drama.heat_value, drama.totalHeatValue),
        avgRating: readNumber(drama.avg_rating, drama.rating, drama.avgRating),
        actorAvatarUrls: actorAvatarUrls.length ? actorAvatarUrls : undefined,
    };
};


const readStoredTitle = (item: Partial<TheaterBannerConfigItem> & Record<string, unknown>) =>
    readString(item.title, item.titleOverride);

const readStoredDescription = (item: Partial<TheaterBannerConfigItem> & Record<string, unknown>) =>
    readString(item.description, item.descriptionOverride);

export const defaultBannerConfig = (): TheaterBannerConfig => ({
    enabled: false,
    items: [],
});

/**
 * 保存时校验：
 * - 无剧集 → 强制关闭（不允许空 Banner 开着）
 * - 有剧集 → 尊重运营手动开关
 */
export const resolveBannerEnabled = (itemCount: number, enabled?: boolean) =>
    itemCount > 0 && Boolean(enabled);

export const parseBannerConfigValue = (value: unknown): TheaterBannerConfig => {
    if (!value || typeof value !== 'object') return defaultBannerConfig();

    const raw = value as Partial<TheaterBannerConfig>;
    const items = Array.isArray(raw.items) ? raw.items : [];
    const parsedItems = items
        .filter((item): item is TheaterBannerConfigItem =>
            Boolean(item && typeof item === 'object' && item.dramaId),
        )
        .map((item, index) => ({
            ...item,
            dramaId: String(item.dramaId),
            sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : index,
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder);

    return {
        enabled: resolveBannerEnabled(parsedItems.length, raw.enabled ?? true),
        items: parsedItems,
    };
};

// ---------------------------------------------------------------------------
// 反显：config → 编辑态
// ---------------------------------------------------------------------------

export const hydrateBannerItems = (
    configItems: TheaterBannerConfigItem[],
    dramaMap: Map<string, DramaItem>,
): BannerEditorItem[] =>
    configItems.map((item, index) => {
        const drama = dramaMap.get(item.dramaId);
        const storedTitle = readStoredTitle(item);
        const storedDescription = readStoredDescription(item);
        const dramaTitle = drama ? getDramaTitle(drama) : undefined;
        const dramaDescription = drama?.description ?? '';

        return {
            dramaId: item.dramaId,
            sortOrder: index,
            drama: drama ?? null,
            missing: !drama,
            snapshot: extractSnapshotFromConfigItem(item),
            // 直接读 config 的 customXxxUrl，不做 URL 比对
            customBannerUrl: item.customBannerUrl || undefined,
            customThumbUrl: item.customThumbUrl || undefined,
            customPreviewVideoUrl: item.customPreviewVideoUrl || undefined,
            // uploadedXxxUrl 始终从 undefined 开始（本次编辑期间才会有）
            titleOverride: !drama ? storedTitle : storedTitle && storedTitle !== dramaTitle ? storedTitle : undefined,
            descriptionOverride: !drama
                ? storedDescription
                : storedDescription && storedDescription !== dramaDescription
                  ? storedDescription
                  : undefined,
        };
    });

// ---------------------------------------------------------------------------
// 序列化：编辑态 → config
// ---------------------------------------------------------------------------

export const serializeBannerItem = (item: BannerEditorItem, sortOrder: number): TheaterBannerConfigItem => {
    const dramaFields = item.drama ? extractDramaBannerFields(item.drama as DramaItem) : null;
    const snapshot = item.snapshot;
    const preview = mergeBannerPreviewFields(item);

    const title = item.titleOverride?.trim() || dramaFields?.title || snapshot?.title || item.dramaId;
    const description = item.descriptionOverride?.trim() || dramaFields?.description || snapshot?.description || '';

    // 本次有上传 → 转 CDN；否则保留上次的自定义；都没有 → undefined（用剧集默认）
    const customBannerUrl = item.uploadedBannerUrl
        ? toCdnUrl(item.uploadedBannerUrl)
        : item.customBannerUrl || undefined;

    const customThumbUrl = item.uploadedThumbUrl ? toCdnUrl(item.uploadedThumbUrl) : item.customThumbUrl || undefined;

    const customPreviewVideoUrl = item.uploadedPreviewVideoUrl
        ? toCdnUrl(item.uploadedPreviewVideoUrl)
        : item.customPreviewVideoUrl || undefined;

    // 客户端渲染的 URL：自定义优先，否则用剧集默认（或 snapshot 兜底）
    const bannerUrl = customBannerUrl || dramaFields?.bannerUrl || snapshot?.bannerUrl || '';

    const thumbUrl =
        customThumbUrl || customBannerUrl || dramaFields?.thumbUrl || snapshot?.thumbUrl || bannerUrl || '';

    const previewVideoUrl =
        customPreviewVideoUrl || dramaFields?.previewVideoUrl || snapshot?.previewVideoUrl || undefined;
    const previewHlsUrl = customPreviewVideoUrl
        ? undefined
        : dramaFields?.previewHlsUrl || snapshot?.previewHlsUrl || undefined;

    const serialized: TheaterBannerConfigItem = {
        dramaId: item.dramaId,
        sortOrder,
        title,
        description,
        bannerUrl: bannerUrl || thumbUrl,
        thumbUrl: thumbUrl || bannerUrl,
    };

    if (customBannerUrl) serialized.customBannerUrl = customBannerUrl;
    if (customThumbUrl) serialized.customThumbUrl = customThumbUrl;
    if (customPreviewVideoUrl) serialized.customPreviewVideoUrl = customPreviewVideoUrl;
    if (previewHlsUrl) serialized.previewHlsUrl = previewHlsUrl;
    if (previewVideoUrl) serialized.previewVideoUrl = previewVideoUrl;
    if (preview?.tags?.length) serialized.tags = preview.tags;
    if (preview?.totalEpisodes != null) serialized.totalEpisodes = preview.totalEpisodes;
    if (preview?.creatorName) serialized.creatorName = preview.creatorName;
    if (preview?.totalPlayCount != null) serialized.totalPlayCount = preview.totalPlayCount;
    if (preview?.totalHeatValue != null) serialized.totalHeatValue = preview.totalHeatValue;
    if (preview?.avgRating != null) serialized.avgRating = preview.avgRating;
    if (preview?.actorAvatarUrls?.length) serialized.actorAvatarUrls = preview.actorAvatarUrls;

    return serialized;
};

export const serializeBannerItems = (items: BannerEditorItem[]): TheaterBannerConfigItem[] =>
    items.map((item, index) => serializeBannerItem(item, index));

export const buildBannerConfigValue = (
    items: BannerEditorItem[],
    settings: Pick<TheaterBannerConfig, 'enabled'>,
): TheaterBannerConfig => ({
    enabled: resolveBannerEnabled(items.length, settings.enabled),
    items: serializeBannerItems(items),
});

// ---------------------------------------------------------------------------
// 显示 URL（编辑态预览用）
// ---------------------------------------------------------------------------

/** 编辑态封面图显示 URL：刚上传 > 自定义 > 剧集默认 */
export const resolveBannerDisplayUrl = (item: BannerEditorItem): string => {
    if (item.uploadedBannerUrl) return item.uploadedBannerUrl;
    if (item.customBannerUrl) return item.customBannerUrl;
    if (!item.drama) return item.snapshot?.bannerUrl || '';
    return resolveDramaListAssetUrl(getDramaBannerUrl(item.drama));
};

/** 编辑态缩略图显示 URL */
export const resolveThumbDisplayUrl = (item: BannerEditorItem): string => {
    if (item.uploadedThumbUrl) return item.uploadedThumbUrl;
    if (item.customThumbUrl) return item.customThumbUrl;
    // 缩略图无自定义时，用封面图兜底
    if (item.uploadedBannerUrl) return item.uploadedBannerUrl;
    if (item.customBannerUrl) return item.customBannerUrl;
    if (!item.drama) return item.snapshot?.thumbUrl || item.snapshot?.bannerUrl || '';
    return resolveDramaListAssetUrl(getDramaCover(item.drama as DramaItem));
};

/** 是否有自定义封面（运营上传或刚上传） */
export const hasCustomBannerImage = (item: BannerEditorItem) => Boolean(item.uploadedBannerUrl || item.customBannerUrl);

/** 是否有自定义缩略图 */
export const hasCustomThumbImage = (item: BannerEditorItem) => Boolean(item.uploadedThumbUrl || item.customThumbUrl);

// ---------------------------------------------------------------------------
// 默认值（用于占位显示和恢复默认对比）
// ---------------------------------------------------------------------------

export const getDefaultBannerImageUrl = (item: BannerEditorItem): string => {
    if (!item.drama) return item.snapshot?.bannerUrl || '';
    return resolveDramaListAssetUrl(getDramaBannerUrl(item.drama));
};

export const getDefaultThumbImageUrl = (item: BannerEditorItem): string => {
    if (!item.drama) return item.snapshot?.thumbUrl || getDefaultBannerImageUrl(item);
    return resolveDramaListAssetUrl(getDramaCover(item.drama as DramaItem));
};

export const getDefaultBannerTitle = (item: BannerEditorItem): string => {
    if (item.drama) return getDramaTitle(item.drama as DramaItem);
    return item.snapshot?.title ?? '';
};

export const getDefaultBannerDescription = (item: BannerEditorItem): string => {
    if (item.drama?.description) return item.drama.description;
    return item.snapshot?.description ?? '';
};

/** input 里显式设置过（哪怕是空串）就算 override，用于是否显示"恢复默认"按钮 */
export const isBannerTitleOverridden = (item: BannerEditorItem) => item.titleOverride !== undefined;

export const isBannerDescriptionOverridden = (item: BannerEditorItem) => item.descriptionOverride !== undefined;

// ---------------------------------------------------------------------------
// 预览字段合并
// ---------------------------------------------------------------------------

export const mergeBannerPreviewFields = (item: BannerEditorItem) => {
    const fromDrama = item.drama ? extractDramaBannerFields(item.drama as DramaItem) : null;
    const snap = item.snapshot;

    if (!fromDrama && !snap) return null;

    return {
        title: fromDrama?.title ?? snap?.title,
        description: fromDrama?.description ?? snap?.description,
        bannerUrl: fromDrama?.bannerUrl,
        thumbUrl: fromDrama?.thumbUrl,
        previewHlsUrl: fromDrama?.previewHlsUrl,
        previewVideoUrl: fromDrama?.previewVideoUrl,
        tags: fromDrama?.tags?.length ? fromDrama.tags : snap?.tags,
        totalEpisodes: fromDrama?.totalEpisodes ?? snap?.totalEpisodes,
        creatorName: fromDrama?.creatorName ?? snap?.creatorName,
        totalPlayCount: fromDrama?.totalPlayCount ?? snap?.totalPlayCount,
        totalHeatValue: fromDrama?.totalHeatValue ?? snap?.totalHeatValue,
        avgRating: fromDrama?.avgRating ?? snap?.avgRating,
        actorAvatarUrls: fromDrama?.actorAvatarUrls?.length ? fromDrama.actorAvatarUrls : snap?.actorAvatarUrls,
    };
};

export const resolveBannerPreviewFields = (item: BannerEditorItem) => mergeBannerPreviewFields(item);

/** 剧集列表 status 为 DELETED 时表示用户已删除 */
export const isDramaDeleted = (drama?: DramaItemRef | DramaItem | null) => {
    if (!drama) return false;
    return normalizeStatus(drama.status ?? drama.audit_status) === 'DELETED';
};

/** 已下架：不宜继续作为客户端 Banner */
export const isDramaOffline = (drama?: DramaItemRef | DramaItem | null) => {
    if (!drama) return false;
    return normalizeStatus(drama.status ?? drama.audit_status) === 'OFFLINE';
};

/** 已配置 Banner 需要从配置中移除的剧集状态 */
export const isBannerDramaUnavailable = (drama?: DramaItemRef | DramaItem | null) =>
    isDramaDeleted(drama) || isDramaOffline(drama);

const shouldPruneBannerItem = (
    item: BannerEditorItem,
    dramaMap: Map<string, DramaItem>,
    selectedResolved: boolean,
) => {
    const drama = dramaMap.get(item.dramaId);
    if (drama && isBannerDramaUnavailable(drama)) return true;
    // 已按「已选 ID」逐条校验仍找不到 → 列表侧已不可见，从配置移除
    if (selectedResolved && !drama) return true;
    return false;
};

// ---------------------------------------------------------------------------
// 条目增删改排序
// ---------------------------------------------------------------------------

/**
 * 用剧集详情合并已选 Banner。
 * - dramas 应是「与已选相关」的剧集（按 ID 校验结果，或当前页里命中的已选项）
 * - selectedResolved=true：表示已完成按 ID 校验，找不到/不可用的项可安全 prune
 */
export const mergeEditorItemsWithCatalog = (
    items: BannerEditorItem[],
    dramas: DramaItem[],
    options?: { selectedResolved?: boolean; /** @deprecated 使用 selectedResolved */ catalogComplete?: boolean },
): BannerEditorItem[] => {
    const dramaMap = new Map(dramas.map((drama) => [drama.id, drama]));
    const selectedResolved = options?.selectedResolved ?? options?.catalogComplete ?? false;

    return items
        .filter((item) => !shouldPruneBannerItem(item, dramaMap, selectedResolved))
        .map((item, index) => {
            const drama = dramaMap.get(item.dramaId);
            if (!drama) {
                return {
                    ...item,
                    sortOrder: index,
                    // 未完成 ID 校验时不要误标 missing（当前页不含该剧很正常）
                    missing: selectedResolved ? true : item.missing,
                };
            }

            const fields = extractDramaBannerFields(drama);

            return {
                ...item,
                sortOrder: index,
                drama,
                missing: false,
                snapshot: {
                    title: fields.title ?? item.snapshot?.title,
                    description: fields.description ?? item.snapshot?.description,
                    tags: fields.tags ?? item.snapshot?.tags,
                    totalEpisodes: fields.totalEpisodes ?? item.snapshot?.totalEpisodes,
                    creatorName: fields.creatorName ?? item.snapshot?.creatorName,
                    totalPlayCount: fields.totalPlayCount ?? item.snapshot?.totalPlayCount,
                    totalHeatValue: fields.totalHeatValue ?? item.snapshot?.totalHeatValue,
                    avgRating: fields.avgRating ?? item.snapshot?.avgRating,
                    actorAvatarUrls: fields.actorAvatarUrls ?? item.snapshot?.actorAvatarUrls,
                    previewHlsUrl: fields.previewHlsUrl ?? item.snapshot?.previewHlsUrl,
                    previewVideoUrl: fields.previewVideoUrl ?? item.snapshot?.previewVideoUrl,
                },
            };
        });
};

export const resolveBannerTitle = (item: BannerEditorItem): string => {
    if (item.titleOverride?.trim()) return item.titleOverride.trim();
    const defaultTitle = getDefaultBannerTitle(item);
    if (defaultTitle) return defaultTitle;
    return item.dramaId;
};

export const resolveBannerDescription = (item: BannerEditorItem): string => {
    if (item.descriptionOverride?.trim()) return item.descriptionOverride.trim();
    return getDefaultBannerDescription(item);
};

export const canAddBannerItem = (items: BannerEditorItem[]) => items.length < THEATER_BANNER_MAX_ITEMS;

export const addBannerItem = (items: BannerEditorItem[], drama: DramaItem): BannerEditorItem[] => {
    if (items.length >= THEATER_BANNER_MAX_ITEMS) return items;
    if (items.some((entry) => entry.dramaId === drama.id)) return items;

    const fields = extractDramaBannerFields(drama);

    return [
        ...items,
        {
            dramaId: drama.id,
            sortOrder: items.length,
            drama,
            snapshot: {
                title: fields.title,
                description: fields.description,
                tags: fields.tags,
                totalEpisodes: fields.totalEpisodes,
                creatorName: fields.creatorName,
                totalPlayCount: fields.totalPlayCount,
                totalHeatValue: fields.totalHeatValue,
                avgRating: fields.avgRating,
                actorAvatarUrls: fields.actorAvatarUrls,
                previewHlsUrl: fields.previewHlsUrl,
                previewVideoUrl: fields.previewVideoUrl,
            },
        },
    ];
};

export const removeBannerItem = (items: BannerEditorItem[], dramaId: string): BannerEditorItem[] =>
    items.filter((item) => item.dramaId !== dramaId).map((item, index) => ({ ...item, sortOrder: index }));

export const updateBannerItem = (
    items: BannerEditorItem[],
    dramaId: string,
    patch: Partial<BannerEditorItem>,
): BannerEditorItem[] => items.map((item) => (item.dramaId === dramaId ? { ...item, ...patch } : item));

export const moveBannerItem = (
    items: BannerEditorItem[],
    index: number,
    direction: 'up' | 'down',
): BannerEditorItem[] => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return items;

    const next = [...items];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next.map((item, sortIndex) => ({ ...item, sortOrder: sortIndex }));
};
